const BUILT_COMMIT = import.meta.env.VITE_BUILD_COMMIT ?? ''
const POLL_INTERVAL_MS = 15_000
const EXACT_OBJECT_ID = /^[0-9a-f]{40}([0-9a-f]{24})?$/

type HealthResponse = {
  commit?: unknown
}

export function shouldReloadForCommit(builtCommit: string, deployedCommit: unknown): boolean {
  if (!EXACT_OBJECT_ID.test(builtCommit) || typeof deployedCommit !== 'string') {
    return false
  }
  return EXACT_OBJECT_ID.test(deployedCommit) && deployedCommit !== builtCommit
}

export async function checkVersionFreshness(
  builtCommit: string = BUILT_COMMIT,
  fetchHealth: typeof fetch = fetch,
  reload: () => void = () => window.location.reload(),
): Promise<boolean> {
  if (!EXACT_OBJECT_ID.test(builtCommit)) {
    return false
  }

  try {
    const response = await fetchHealth('/healthz', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      return false
    }
    const health = (await response.json()) as HealthResponse
    if (!shouldReloadForCommit(builtCommit, health.commit)) {
      return false
    }
    reload()
    return true
  } catch {
    return false
  }
}

export function startVersionFreshness(): () => void {
  if (!EXACT_OBJECT_ID.test(BUILT_COMMIT) || typeof window === 'undefined') {
    return () => undefined
  }

  let checking = false
  let stopped = false
  const check = async () => {
    if (stopped || checking || document.visibilityState === 'hidden') {
      return
    }
    checking = true
    const reloaded = await checkVersionFreshness(BUILT_COMMIT)
    checking = false
    if (reloaded) {
      stopped = true
    }
  }

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      void check()
    }
  }
  const timer = window.setInterval(() => void check(), POLL_INTERVAL_MS)
  window.addEventListener('focus', check)
  document.addEventListener('visibilitychange', onVisible)
  void check()

  return () => {
    stopped = true
    window.clearInterval(timer)
    window.removeEventListener('focus', check)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
